// ============================================
// TourSpotlight.jsx - one tour step at a time for the chat assistant.
// Listens for 'cheddar:tour' { panel, caption, index, total, resolve }.
// Finds [data-tour-id=panel] (first VISIBLE match), dims everything else
// with a huge box-shadow around an outline box, shows a caption with
// Next / Close. Resolves 'next' | 'close' | 'missing'. Esc = close,
// Enter / Right = next. Repositions on resize/scroll.
// ============================================
import { useEffect, useState, useCallback } from 'react'

function findPanel(id) {
  const els = document.querySelectorAll(`[data-tour-id="${(window.CSS && CSS.escape) ? CSS.escape(id) : id}"]`)
  for (const el of els) {
    const r = el.getBoundingClientRect()
    if (r.width > 0 && r.height > 0) return el
  }
  return null
}

export default function TourSpotlight() {
  const [step, setStep] = useState(null)   // { panel, caption, index, total, resolve, rect, label }

  useEffect(() => {
    const on = (e) => {
      const d = e?.detail
      if (!d || typeof d.resolve !== 'function') return
      const el = findPanel(d.panel)
      if (!el) { d.resolve('missing'); return }
      setStep(prev => {
        if (prev) { try { prev.resolve('next') } catch {} }
        return { ...d, rect: el.getBoundingClientRect(), label: el.getAttribute('data-tour-label') || d.panel }
      })
    }
    window.addEventListener('cheddar:tour', on)
    return () => window.removeEventListener('cheddar:tour', on)
  }, [])

  const answer = useCallback((v) => {
    setStep(prev => { if (prev) { try { prev.resolve(v) } catch {} } return null })
  }, [])

  useEffect(() => {
    if (!step) return
    const re = () => {
      const el = findPanel(step.panel)
      if (el) setStep(s => (s ? { ...s, rect: el.getBoundingClientRect() } : s))
    }
    const key = (e) => {
      if (e.key === 'Escape') answer('close')
      else if (e.key === 'Enter' || e.key === 'ArrowRight') answer('next')
    }
    window.addEventListener('resize', re)
    window.addEventListener('scroll', re, true)
    window.addEventListener('keydown', key)
    return () => {
      window.removeEventListener('resize', re)
      window.removeEventListener('scroll', re, true)
      window.removeEventListener('keydown', key)
    }
  }, [step?.panel, answer])

  if (!step) return null
  const r = step.rect
  const pad = 4
  const box = { left: r.left - pad, top: r.top - pad, width: r.width + pad * 2, height: r.height + pad * 2 }
  // Caption below the panel if it fits, else above, else inside the top.
  const capH = 90
  const vw = window.innerWidth, vh = window.innerHeight
  let capTop = box.top + box.height + 8
  if (capTop + capH > vh) capTop = box.top - capH - 8
  if (capTop < 8) capTop = Math.max(8, box.top + 8)
  const capW = Math.min(320, vw - 16)
  const capLeft = Math.max(8, Math.min(vw - capW - 8, box.left))
  const last = step.total ? step.index >= step.total - 1 : true
  return (
    <div className="tour-root" onMouseDown={(e) => e.stopPropagation()}>
      <div className="tour-hole" style={box} />
      <div className="tour-caption" style={{ top: capTop, left: capLeft, width: capW }}>
        <div className="tour-title">
          {step.label}
          {step.total > 1 && <span className="tour-count">{(step.index ?? 0) + 1}/{step.total}</span>}
        </div>
        {step.caption && <div className="tour-text">{step.caption}</div>}
        <div className="tour-actions">
          <button type="button" className="scc-btn" onClick={() => answer('close')}>Close</button>
          <button type="button" className="scc-btn scc-ok" onClick={() => answer('next')}>{last ? 'Done' : 'Next'}</button>
        </div>
      </div>
    </div>
  )
}
